import activateProductIpm from './activateProductIpm.js';
import activateProductWeb from './activateProductWeb.js';

/**
 * Handler when product activation completed
 * @param {object} cart Cart object
 * @param {object[]} products Products being activated
 * @param {'success'|'failure'} status Status of the activation
 * @param {object} dependencies
 * @param {object} dependencies.store Vuex store
 * @param {object} dependencies.tracking Tracking
 */
const onActivationFinished = (cart, products, status, { store, tracking }) => {
	products.forEach((product) => {
		if (status === 'success') {
			store.commit('SET_ACTIVATION', {
				id: product.id,
				status: 'Activation: Success',
				successCount: 1
			});
		} else if (status === 'failure') {
			store.commit('SET_ACTIVATION', {
				id: product.id,
				status: 'Activation: Fail',
				failCount: 1
			});
		}
		tracking.productActivation(cart, product);
	});
};

/**
 * Browse the store state and determine the current theme platform
 * @param {object} dependencies
 * @param {object} dependencies.store Vuex store
 * @returns {string}
 */
const determineActivationPlatform = ({ store }) => {
	return store.state.theme.tracking?.platform || 'ipm';
};

/**
 * Run activation method for given configuration and products
 * @param {object} cart Cart object
 * @param {object} activation Activation object with correct properties by license type
 * @param {object[]} products Array of products for the given activation configuration
 * @param {object} dependencies
 * @returns {Promise}
 */
const runActivation = async (cart, activation, products, dependencies) => {
	try {
		const platform = determineActivationPlatform(dependencies);
		const activationFunction = platform === 'web' ? activateProductWeb : activateProductIpm;
		await activationFunction(activation, products);
		onActivationFinished(cart, products, 'success', dependencies);
	} catch {
		onActivationFinished(cart, products, 'failure', dependencies);
	}
};

/**
 * Create object with unique licenses and all it's products
 * @param {object} order Order object
 * @returns {object[]} Array of license objects with products
 */
const getLicenses = (order) => {
	const licenses = order.products.reduce((licenses, product) => {
		if (Array.isArray(product.fulfillment?.license) && product.fulfillment?.license.length) {
			product.fulfillment.license.forEach(({ content: key, contentType: type }) => {
				if (['walletkey', 'activationkey'].includes(type)) {
					const duplicateLicense = licenses[key] || {};
					licenses[key] = {
						key,
						type,
						products: [...duplicateLicense.products || [], product]
					};
				}
			});
		}
		return licenses;
	}, {});
	return Object.values(licenses);
};

/**
 * Activate all products with returned licenses
 * @param {object} cart Cart object
 * @param {object} dependencies
 * @returns {Promise}
 */
const activateProducts = (cart, dependencies) => {
	const licenses = getLicenses(cart.order);
	return Promise.all(licenses.map((license) => {
		if (license.type === 'walletkey') {
			return runActivation(cart, {
				walletKey: license.key,
				correlationID: cart.order.aswparam.object.p_crd
			}, license.products, dependencies);
		} else {
			return Promise.all(license.products.map((product) => {
				return runActivation(cart, {
					activationKey: license.key,
					productSKU: product.sku.slice(0, 3)
				}, [product], dependencies);
			}));
		}
	}));
};

/**
 * Export the Vue plugin install method
 * @param {object} Vue Vue instance
 */
export const install = (Vue) => {

	/**
	 * Activate all products in the cart
	 * @param {object} cart Cart object
	 * @returns {Promise} Fulfilled when all products activation completed (both success or failure)
	 */
	Vue.prototype.activateProducts = function(cart) {
		return activateProducts(cart, {
			store: this.$store,
			tracking: this.tracking
		});
	};

};

export default {
	install
};
